import type { Light, LightColor, LightPattern } from "./types.js";

/**
 * Light-character helpers. Two jobs:
 *   1) Animate — given a Light and the current sim-time seconds, return
 *      whether the light is on or off and at what intensity, so the
 *      PPI / chart renderer can pulse it at the correct rhythm.
 *   2) Describe — return a long-form English description of the
 *      character (e.g. "Iso.WRG.4s" → "Isophase white-red-green, 4s
 *      period") for the in-app mark popover.
 *
 * Determinism: pulse phase is derived purely from sim-time, not
 * wall-clock, so replays paint the same flashes at the same moments.
 */

export interface LightFrame {
  /** Whether the light is currently on. */
  on: boolean;
  /** Brightness 0..1 — used to add a soft fade-in/fade-out on the
   *  rising/falling edge of each flash so it doesn't read as a hard
   *  one-bit toggle. Solid 1.0 mid-flash. */
  intensity: number;
  /** Colour to paint when on. CSS-friendly. */
  color: string;
}

const COLORS: Record<LightColor, string> = {
  W: "#fef3c7", // warm white — slightly amber to match brand
  R: "#ef4444",
  G: "#22c55e",
  Y: "#facc15",
};

/** Standard short flash duration for "Fl" pulses in seconds. */
const FLASH_SEC = 0.5;
/** Long flash duration in "Q(N)+LFl" cycles. */
const LONG_FLASH_SEC = 2;
/** Short pulse on a quick-flashing light. */
const QUICK_ON_SEC = 0.3;
const QUICK_PERIOD_SEC = 1; // 60 fpm
/** Per-element extents for Morse (short-flash radio Morse, generous so
 *  the eye reads dot vs dash). */
const MORSE_DOT_SEC = 0.4;
const MORSE_DASH_SEC = MORSE_DOT_SEC * 3;
const MORSE_GAP_SEC = MORSE_DOT_SEC;

const MORSE: Record<string, string> = {
  A: ".-",
  B: "-...",
  C: "-.-.",
  D: "-..",
  E: ".",
  F: "..-.",
  G: "--.",
  H: "....",
  I: "..",
  J: ".---",
  K: "-.-",
  L: ".-..",
  M: "--",
  N: "-.",
  O: "---",
  P: ".--.",
  Q: "--.-",
  R: ".-.",
  S: "...",
  T: "-",
  U: "..-",
  V: "...-",
  W: ".--",
  X: "-..-",
  Y: "-.--",
  Z: "--..",
};

/**
 * Compute the on/off frame for a light at a given sim-time.
 * Pure function — same inputs → same output.
 */
export function lightFrame(light: Light, simSeconds: number): LightFrame {
  const baseColor = COLORS[light.color];
  const pattern = light.pattern;
  const phase = wrap(simSeconds, periodFor(pattern));
  const on = isOn(pattern, phase);
  // Soft 50ms ramp on each edge so the pulse doesn't snap.
  const intensity = on ? edgeRamp(pattern, phase) : 0;
  return { on, intensity, color: baseColor };
}

/**
 * Total period of one cycle in seconds. Used to wrap sim-time.
 */
function periodFor(p: LightPattern): number {
  switch (p.kind) {
    case "flash":
      return p.periodSec;
    case "iso":
      return p.periodSec;
    case "quick":
      return QUICK_PERIOD_SEC;
    case "quick-grouped":
      return p.periodSec;
    case "quick-grouped-long":
      return p.periodSec;
    case "morse":
      return p.periodSec;
  }
}

function isOn(p: LightPattern, phase: number): boolean {
  switch (p.kind) {
    case "flash":
      return phase < (p.flashSec ?? FLASH_SEC);
    case "iso":
      return phase < p.periodSec / 2;
    case "quick":
      return wrap(phase, QUICK_PERIOD_SEC) < QUICK_ON_SEC;
    case "quick-grouped": {
      const group = QUICK_PERIOD_SEC * p.flashes;
      if (phase >= group) return false;
      return wrap(phase, QUICK_PERIOD_SEC) < QUICK_ON_SEC;
    }
    case "quick-grouped-long": {
      const groupEnd = QUICK_PERIOD_SEC * p.flashes;
      if (phase < groupEnd) {
        return wrap(phase, QUICK_PERIOD_SEC) < QUICK_ON_SEC;
      }
      // After the quick group, one long flash starts after a short
      // dark gap.
      const longStart = groupEnd + 0.6;
      const longEnd = longStart + LONG_FLASH_SEC;
      return phase >= longStart && phase < longEnd;
    }
    case "morse": {
      const code = MORSE[p.char.toUpperCase()];
      if (code === undefined) return false;
      // Walk the elements: each dot is on for MORSE_DOT_SEC, dash for
      // MORSE_DASH_SEC, with MORSE_GAP_SEC of dark between elements.
      // Whatever's left of the period is dark trailing time.
      let t = 0;
      for (const sym of code) {
        const onLen = sym === "." ? MORSE_DOT_SEC : MORSE_DASH_SEC;
        if (phase >= t && phase < t + onLen) return true;
        t += onLen + MORSE_GAP_SEC;
      }
      return false;
    }
  }
}

function edgeRamp(p: LightPattern, phase: number): number {
  // 50ms fade-in / fade-out approximation. Compute distance to the
  // nearest edge of the current "on" interval and ramp up/down.
  // Conservative: full intensity except in the first/last 50ms of
  // each on-interval. Without a structured edge list we approximate
  // by checking neighbour phases.
  const period = periodFor(p);
  const PROBE_SEC = 0.05;
  const before = isOn(p, wrap(phase - PROBE_SEC, period));
  const after = isOn(p, wrap(phase + PROBE_SEC, period));
  if (!before) return 0.7; // rising edge
  if (!after) return 0.7; // falling edge
  return 1.0;
}

function wrap(v: number, period: number): number {
  return ((v % period) + period) % period;
}

/**
 * Long-form English description of a light character — shown next to
 * the IALA notation in the mark popover so students see both the
 * abbreviation they'd find on a chart and what it means.
 */
export function describeLight(light: Light): string {
  const colorLabel: Record<LightColor, string> = {
    W: "white",
    R: "red",
    G: "green",
    Y: "yellow",
  };
  const colour = colorLabel[light.color];
  const p = light.pattern;
  switch (p.kind) {
    case "flash":
      return `Flashing ${colour}, every ${p.periodSec}s`;
    case "iso":
      return `Isophase ${colour}, ${p.periodSec}s period (light = dark)`;
    case "quick":
      return `Quick flashing ${colour} (continuous, ~60 fpm)`;
    case "quick-grouped":
      return `${p.flashes} quick flashes ${colour}, ${p.periodSec}s period`;
    case "quick-grouped-long":
      return `${p.flashes} quick flashes plus one long flash ${colour}, ${p.periodSec}s period (south cardinal)`;
    case "morse":
      return `Morse "${p.char.toUpperCase()}" ${colour}, ${p.periodSec}s period`;
  }
}
